import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import ExternalLink from '@groovehq/internal-design-system/lib/assets/icons/ExternalLink'
import { KeyValue, CollapsibleKeyValue } from 'components/KeyValues'
import NavigatableIntegrationWidgetCard from 'components/integrations/NavigatableIntegrationWidgetCard'
import {
  FULFILLMENT_DISPLAY_STATUS_TEXT,
  ORDER_DISPLAY_FINANCIAL_STATUS_TEXT,
} from 'ducks/integrations/shopify/constants'
import { strftime } from 'util/date'
import { shopifyMoneyString } from 'util/currency'
import { selectOrderForId } from 'ducks/integrations/shopify/selectors'
import CancelOrderButton from './CancelOrderButton'
import RefundOrderButton from './RefundOrderButton'
import DuplicateOrderButton from './DuplicateOrderButton'
import ConditionalRow from './ConditionalRow'
import FulfillmentRow from './FulfillmentRow'
import OrderLineItem from './OrderLineItem'

export default function ShopifyDetail({
  navigatableState,
  doNavigatableClearState,
}) {
  const { orderId = null, integration = {} } = navigatableState || {}

  const orderShortId = orderId && orderId.split('/').pop()
  // we pass the order id and then get it from the state - this allows the id to be stored
  // in the url and survices a refresh
  const order = useSelector(state =>
    selectOrderForId(state, { orderId: orderShortId })
  )

  const lineItems = useMemo(
    () => {
      return order ? order.lineItems?.edges.map(edge => edge.node) : []
    },
    [order]
  )

  const cancelledAtString = useMemo(
    () => {
      if (order && order.isCancelled) {
        return strftime('%d %b %Y @ %l:%M%p', order.cancelledAt)
      }
      return null
    },
    [order]
  )

  if (!navigatableState) return null
  if (!order) return null

  const {
    name,
    createdAt,
    requiresShipping,
    fulfillments,
    displayFinancialStatus,
    displayFulfillmentStatus,
    totalShippingPriceSet,
    currentSubtotalPriceSet,
    currentTotalTaxSet,
    refundAmount,
    refundCurrencyCode,
    unpaid,
    currentTotalPriceSet,
    isCancelled,
    currentTotalDiscountsSet,
    refundable,
    isCancellable,
    billingAddressMatchesShippingAddress,
    billingAddress,
    shippingAddress,
  } = order

  const hasBillingAddress =
    billingAddress && !!billingAddress?.formatted?.length
  const hasShippingAddress =
    shippingAddress && !!shippingAddress?.formatted?.length
  const hasFulfillmentLineItems = !!fulfillments?.length

  const orderUrl = `https://${
    integration.storeDomain
  }.myshopify.com/admin/orders/${orderShortId}`

  return (
    <NavigatableIntegrationWidgetCard.Detail
      unifiedTitleIconButton
      onBack={doNavigatableClearState}
      title="back"
    >
      <div className="grui p-7 shopifyV2-order-data">
        <KeyValue
          title="Order"
          value={
            <a href={orderUrl} target="_blank" rel="noopener noreferrer">
              {name}
              <ExternalLink className="shopifyV2-externalLink-icon" />
            </a>
          }
        />
        <KeyValue
          title="Order date"
          value={strftime('%d %b %Y @ %l:%M%p', createdAt)}
        />
        <KeyValue
          title="Payment status"
          value={ORDER_DISPLAY_FINANCIAL_STATUS_TEXT[displayFinancialStatus]}
        />
        <KeyValue
          title="Fulfillment status"
          value={FULFILLMENT_DISPLAY_STATUS_TEXT[displayFulfillmentStatus]}
        />
        {isCancelled && (
          <KeyValue title="Cancelled" value={cancelledAtString} />
        )}
        {(!requiresShipping ||
          (requiresShipping && hasFulfillmentLineItems)) && (
          <KeyValue
            title="Shipping"
            missingValue={!requiresShipping && 'No shipping required'}
            grouped={requiresShipping && hasFulfillmentLineItems}
          >
            {requiresShipping &&
              hasFulfillmentLineItems && (
                <>
                  {fulfillments.map(fulfillment => (
                    <FulfillmentRow
                      fulfillment={fulfillment}
                      key={fulfillment.id}
                    />
                  ))}
                </>
              )}
          </KeyValue>
        )}
        {requiresShipping && (
          <>
            {hasShippingAddress && (
              <CollapsibleKeyValue
                title="Shipping address"
                items={shippingAddress.formatted}
              />
            )}
            {hasBillingAddress && (
              <CollapsibleKeyValue
                title="Billing address"
                missingValue={
                  billingAddressMatchesShippingAddress &&
                  'Same as shipping address'
                }
                items={
                  !billingAddressMatchesShippingAddress &&
                  billingAddress.formatted
                }
              />
            )}
          </>
        )}
      </div>
      <div className="shopifyV2-header-label">Order details</div>
      <div className="grui p-7">
        <table className="shopifyV2-order-table">
          {lineItems.map(lineItem => (
            <OrderLineItem
              lineItem={lineItem}
              integration={integration}
              key={lineItem.id}
            />
          ))}
          <tbody className="shopifyV2-order-table-sub">
            {requiresShipping && (
              <ConditionalRow text="Shipping" obj={totalShippingPriceSet} />
            )}
            <ConditionalRow text="Subtotal" obj={currentSubtotalPriceSet} />
            <ConditionalRow text="Tax" obj={currentTotalTaxSet} />
            <ConditionalRow
              text="Discount"
              obj={currentTotalDiscountsSet}
              negative
            />
          </tbody>
          <tfoot>
            <tr>
              <td>
                <KeyValue title="Total" missingValue={null} />
              </td>
              <td className="grui text-right shopifyV2-currency">
                {shopifyMoneyString(currentTotalPriceSet.shopMoney)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="shopifyV2-footer">
        {refundable && (
          <RefundOrderButton orderId={orderShortId} integration={integration} />
        )}
        <DuplicateOrderButton order={order} integration={integration} />
        {isCancellable && (
          <CancelOrderButton
            integration={integration}
            orderId={orderShortId}
            unpaid={unpaid}
            refundAmount={refundAmount}
            currencyCode={refundCurrencyCode}
          />
        )}
      </div>
    </NavigatableIntegrationWidgetCard.Detail>
  )
}
